import React, { useMemo } from 'react';
import {
    Autocomplete,
    Button,
    Box,
    Paper,
    styled,
    TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PayComponent } from 'routes/HumanResourcesModule/PayState/helpers/constants';
import { orgChartHrFormatter } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { OrgChartHr } from 'routes/HumanResourcesModule/PayState/helpers/types';
import PaymentsIcon from '@mui/icons-material/Payments';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

const SUPPORTED_PAY_COMPONENTS_WITH_ICONS = {
    [PayComponent.SUPPLEMENTARY_HOURS]: <MoreTimeIcon fontSize="large" />,
    [PayComponent.COMPLEMENTARY_HOURS]: <TimelapseIcon fontSize="large" />,
    [PayComponent.ASTREINTES]: <EventAvailableIcon fontSize="large" />,
    [PayComponent.PONCTUAL_PRIMES]: <PaymentsIcon fontSize="large" />,
} as const;

const StyFirstStepPaper = styled(Paper, {
    name: 'StyFirstStepPaper',
})(({ theme }) => ({
    padding: '1rem',
    position: 'absolute',
    left: '1rem',
    right: '1rem',
    top: '50%',
    backgroundColor: theme.palette.bg.light,
    transform: 'translateY(-50%)',
}));
const PayComponentSelectionButton = styled(Button, {
    name: 'PayComponentSelectionButton',
    shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ selected, theme }) => ({
    width: 'calc(50% - 1rem)',
    height: '120px',
    '&:nth-of-type(even)': {
        marginLeft: '1rem',
    },
    '&:nth-of-type(odd)': {
        marginRight: '1rem',
    },
    marginBottom: '2rem',
    fontSize: '0.8rem',
    flexDirection: 'column',
    color: 'black',
    borderWidth: selected ? 3 : 1,
    '&:hover': {
        borderWidth: selected ? 3 : 1,
    },
    backgroundColor: selected
        ? theme.palette.primary.background
        : theme.palette.grey[50],
}));

type Props = {
    orgChartHrs: OrgChartHr[];
    selectedOrgChartHr: string;
    setSelectedOrgChartHr: DispatchState<string>;
    selectedPayComponent: string;
    setSelectedPayComponent: DispatchState<string>;
};

export default function AddEnvelopePayComponentHypothesisModalFirstStep({
    orgChartHrs,
    selectedOrgChartHr,
    setSelectedOrgChartHr,
    selectedPayComponent,
    setSelectedPayComponent,
}: Props): React.ReactElement {
    const { t } = useTranslation();

    const orgChartHrOptions = useMemo(
        () =>
            orgChartHrs.map((o) => ({
                label: orgChartHrFormatter({
                    orgChartHrName: o.name,
                    orgChartHrMapping: o.orgChartHrMapping?.title,
                }),
                value: o.softwareId,
            })),
        [orgChartHrs],
    );

    const selectedOrgChartHrOption = useMemo(
        () => orgChartHrOptions.find((o) => o.value === selectedOrgChartHr),
        [orgChartHrOptions, selectedOrgChartHr],
    );

    return (
        <Box sx={{ height: '30rem' }}>
            <StyFirstStepPaper>
                {Object.entries(SUPPORTED_PAY_COMPONENTS_WITH_ICONS).map(
                    ([pc, icon]) => (
                        <PayComponentSelectionButton
                            key={pc}
                            variant="outlined"
                            onClick={() => setSelectedPayComponent(pc)}
                            selected={pc === selectedPayComponent}
                        >
                            {icon}
                            {pc}
                        </PayComponentSelectionButton>
                    ),
                )}
                <Autocomplete
                    id="org-chart-select"
                    options={orgChartHrOptions}
                    value={selectedOrgChartHrOption}
                    onChange={(_, v) => setSelectedOrgChartHr(v?.value ?? '')}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t(
                                'humanResources.simulation.addAgent.orgChartHr',
                            )}
                            placeholder={t(
                                'humanResources.simulation.envelope.creationModal.orgChartHrSearchPlaceholder',
                            )}
                        />
                    )}
                />
            </StyFirstStepPaper>
        </Box>
    );
}
